"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Paper,
  Badge,
  Box,
  ColorSwatch,
  SimpleGrid,
  ThemeIcon,
  Card,
  Divider,
  Button,
  Image,
  Accordion,
  ActionIcon,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconShirt,
  IconPalette,
  IconUsers,
  IconSparkles,
  IconArrowRight,
  IconHanger,
  IconQuestionMark,
  IconChevronRight,
  IconChevronLeft,
} from "@tabler/icons-react";
import Link from "next/link";
import { useState } from "react";
import { AestheticType, getAestheticFAQs } from "../aestheticData";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";

interface AestheticTypeContentProps {
  typeData: AestheticType;
  lastUpdated: string;
}

// Type-specific accent colors for visual interest
const typeAccentColors: Record<string, { primary: string; gradient: string; light: string }> = {
  "old-money": { primary: "indigo.7", gradient: "linear-gradient(135deg, #1B365D 0%, #C19A6B 100%)", light: "indigo.0" },
  "clean-girl": { primary: "teal.6", gradient: "linear-gradient(135deg, #0D9488 0%, #F5F5DC 100%)", light: "teal.0" },
  "dark-feminine": { primary: "grape.8", gradient: "linear-gradient(135deg, #4A0E0E 0%, #301934 100%)", light: "grape.0" },
  coquette: { primary: "pink.5", gradient: "linear-gradient(135deg, #FFB6C1 0%, #E6E6FA 100%)", light: "pink.0" },
  "quiet-luxury": { primary: "gray.7", gradient: "linear-gradient(135deg, #36454F 0%, #C2B280 100%)", light: "gray.0" },
  "indie-sleaze": { primary: "red.7", gradient: "linear-gradient(135deg, #722F37 0%, #808080 100%)", light: "red.0" },
  "coastal-grandmother": { primary: "cyan.6", gradient: "linear-gradient(135deg, #6F8FAF 0%, #FAF9F6 100%)", light: "cyan.0" },
  balletcore: { primary: "pink.4", gradient: "linear-gradient(135deg, #E8CEBF 0%, #FFFFFF 100%)", light: "pink.0" },
  "mob-wife": { primary: "yellow.8", gradient: "linear-gradient(135deg, #000000 0%, #FFD700 100%)", light: "yellow.0" },
  cottagecore: { primary: "green.6", gradient: "linear-gradient(135deg, #9DC183 0%, #FFFACD 100%)", light: "green.0" },
  minimalist: { primary: "gray.8", gradient: "linear-gradient(135deg, #000000 0%, #FFFFFF 100%)", light: "gray.0" },
  streetwear: { primary: "orange.7", gradient: "linear-gradient(135deg, #000000 0%, #39FF14 100%)", light: "orange.0" },
};

export default function AestheticTypeContent({ typeData, lastUpdated }: AestheticTypeContentProps) {
  const [currentOutfitIndex, setCurrentOutfitIndex] = useState(0);
  const accentColors = typeAccentColors[typeData.id] || { primary: "violet.7", gradient: "linear-gradient(135deg, #7C3AED 0%, #DA77F2 100%)", light: "violet.0" };
  const faqs = getAestheticFAQs(typeData.id);

  return (
    <Container size="lg" py="xl">
      {/* Hero Section with Gradient Banner */}
      <Box
        p={{ base: 24, md: 48 }}
        mb="xl"
        style={{
          background: accentColors.gradient,
          borderRadius: "var(--mantine-radius-lg)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Elements */}
        <Box
          style={{
            position: "absolute",
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          style={{
            position: "absolute",
            bottom: -30,
            left: "20%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Group gap="sm" mb={16}>
          <Badge size="lg" variant="light" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
            Aesthetic Style Guide
          </Badge>
          <Badge size="sm" variant="light" style={{ background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.9)" }}>
            Updated {new Date(lastUpdated).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
          </Badge>
        </Group>
        <Title order={1} fz={{ base: 32, md: 48 }} fw={300} mb={16} style={{ letterSpacing: -1, color: "white" }}>
          {typeData.name} Aesthetic
        </Title>
        <Text fz="xl" maw={600} mb={24} style={{ color: "rgba(255,255,255,0.9)" }} fs="italic">
          "{typeData.tagline}"
        </Text>

        {/* Quick Stats */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={16}>
          <Paper p="md" radius="md" style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)" }}>
            <Text fz="xs" c="white" style={{ opacity: 0.8 }}>Key Vibe</Text>
            <Text fz="sm" fw={600} c="white">{typeData.characteristics[0]}</Text>
          </Paper>
          <Paper p="md" radius="md" style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)" }}>
            <Text fz="xs" c="white" style={{ opacity: 0.8 }}>Must-Have</Text>
            <Text fz="sm" fw={600} c="white">{typeData.keyPieces[0]}</Text>
          </Paper>
          <Paper p="md" radius="md" style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)" }}>
            <Text fz="xs" c="white" style={{ opacity: 0.8 }}>Signature Color</Text>
            <Text fz="sm" fw={600} c="white">{typeData.colorPalette[0].name}</Text>
          </Paper>
          <Paper p="md" radius="md" style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)" }}>
            <Text fz="xs" c="white" style={{ opacity: 0.8 }}>Style Icon</Text>
            <Text fz="sm" fw={600} c="white">{typeData.celebrities[0].split(" ")[0]}</Text>
          </Paper>
        </SimpleGrid>
      </Box>

      {/* Hero Image */}
      <Box mb="xl" style={{ marginTop: -24 }}>
        <Image
          src={`/aesthetic/types/${typeData.id}-hero.jpg`}
          alt={`${typeData.name} aesthetic style guide featuring ${typeData.colorPalette[0].name.toLowerCase()} tones, ${typeData.keyPieces[0].toLowerCase()}, and ${typeData.characteristics[0].toLowerCase()} vibes`}
          radius="md"
          fallbackSrc={`https://placehold.co/1200x600/f5f5f5/666?text=${encodeURIComponent(typeData.name + ' Aesthetic')}`}
        />
      </Box>

      {/* Color Palette */}
      <Paper shadow="xs" p="lg" radius="md" mb="xl" withBorder>
        <Group gap="xs" mb="md">
          <ThemeIcon size="md" variant="light" color={accentColors.primary.split(".")[0]}>
            <IconPalette size={16} />
          </ThemeIcon>
          <Title order={2} fz="lg">
            {typeData.name} Color Palette
          </Title>
        </Group>
        <Text c="dimmed" mb="md">
          These colors define the {typeData.name} aesthetic. Build your wardrobe around this palette for a cohesive look.
        </Text>
        <Group gap="lg" wrap="wrap">
          {typeData.colorPalette.map((color) => (
            <Stack key={color.name} gap={4} align="center">
              <ColorSwatch color={color.hex} size={56} radius="md" />
              <Text size="xs" c="dimmed" fw={500}>
                {color.name}
              </Text>
            </Stack>
          ))}
        </Group>
      </Paper>

      {/* Description */}
      <Paper shadow="xs" p="lg" radius="md" mb="xl" withBorder>
        <Title order={2} fz="xl" mb="md">
          What is the {typeData.name} Aesthetic?
        </Title>
        <Text style={{ lineHeight: 1.8 }}>{typeData.description}</Text>
        {typeData.seasonConnection && (
          <Box mt="md" p="sm" bg={accentColors.light} style={{ borderRadius: 8 }}>
            <Text size="sm" c={accentColors.primary}>
              <strong>Color Season Connection:</strong> {typeData.seasonConnection}
            </Text>
          </Box>
        )}
      </Paper>

      {/* Characteristics */}
      <Paper shadow="xs" p="lg" radius="md" mb="xl" withBorder>
        <Group gap="xs" mb="md">
          <ThemeIcon size="md" variant="light" color={accentColors.primary.split(".")[0]}>
            <IconSparkles size={16} />
          </ThemeIcon>
          <Title order={2} fz="lg">
            Key Characteristics of {typeData.name}
          </Title>
        </Group>
        <Text c="dimmed" mb="md">
          These elements define the {typeData.name} aesthetic. The more you incorporate, the stronger your look.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
          {typeData.characteristics.map((char, i) => (
            <Group key={i} gap="xs" wrap="nowrap">
              <ThemeIcon size="sm" variant="light" color={accentColors.primary.split(".")[0]} radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
              <Text size="sm">{char}</Text>
            </Group>
          ))}
        </SimpleGrid>
      </Paper>

      {/* Key Pieces */}
      <Paper shadow="xs" p="lg" radius="md" mb="xl" withBorder>
        <Group gap="xs" mb="md">
          <ThemeIcon size="md" variant="light" color={accentColors.primary.split(".")[0]}>
            <IconHanger size={16} />
          </ThemeIcon>
          <Title order={2} fz="lg">
            Essential {typeData.name} Wardrobe Pieces
          </Title>
        </Group>
        <Text c="dimmed" mb="md">
          Build your {typeData.name} wardrobe starting with these key pieces. They're the foundation of the aesthetic.
        </Text>
        <Group gap="xs" wrap="wrap">
          {typeData.keyPieces.map((piece, i) => (
            <Badge key={i} variant="outline" color="gray" size="lg">
              {piece}
            </Badge>
          ))}
        </Group>
      </Paper>

      {/* Outfit Ideas with Carousel */}
      <Paper shadow="xs" p="lg" radius="md" mb="xl" withBorder bg={accentColors.light}>
        <Group justify="space-between" mb="md">
          <Box>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="md" variant="light" color={accentColors.primary.split(".")[0]}>
                <IconShirt size={16} />
              </ThemeIcon>
              <Title order={2} fz="lg">
                {typeData.name} Outfit Ideas
              </Title>
            </Group>
            <Text c="dimmed" size="sm">
              {typeData.outfitIdeas.length} curated looks to inspire your {typeData.name} style
            </Text>
          </Box>
          <Group gap={8}>
            <ActionIcon
              variant="filled"
              color={accentColors.primary.split(".")[0]}
              size="lg"
              onClick={() => setCurrentOutfitIndex((i) => (i - 1 + typeData.outfitIdeas.length) % typeData.outfitIdeas.length)}
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Badge size="lg" variant="light" color={accentColors.primary.split(".")[0]}>
              {currentOutfitIndex + 1} / {typeData.outfitIdeas.length}
            </Badge>
            <ActionIcon
              variant="filled"
              color={accentColors.primary.split(".")[0]}
              size="lg"
              onClick={() => setCurrentOutfitIndex((i) => (i + 1) % typeData.outfitIdeas.length)}
            >
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          {/* Outfit Image */}
          <Box>
            <Image
              src={`/aesthetic/types/${typeData.id}-outfit-${currentOutfitIndex + 1}.jpg`}
              alt={`${typeData.name} aesthetic outfit: ${typeData.outfitIdeas[currentOutfitIndex].name} featuring ${typeData.outfitIdeas[currentOutfitIndex].pieces.slice(0, 2).join(" and ")}`}
              radius="md"
              fallbackSrc={`https://placehold.co/600x700/f5f5f5/666?text=${encodeURIComponent(typeData.outfitIdeas[currentOutfitIndex].name)}`}
              style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}
            />
          </Box>

          {/* Outfit Details */}
          <Card padding="xl" radius="md" withBorder>
            <Badge mb="md" color={accentColors.primary.split(".")[0]} variant="light">
              Look {currentOutfitIndex + 1}
            </Badge>
            <Title order={3} fz={22} fw={500} mb={8}>
              {typeData.outfitIdeas[currentOutfitIndex].name}
            </Title>
            <Text fz="sm" c="dimmed" mb="xl">
              {typeData.outfitIdeas[currentOutfitIndex].description}
            </Text>

            <Text fz="sm" fw={600} mb={12}>Key Pieces:</Text>
            <Stack gap={8}>
              {typeData.outfitIdeas[currentOutfitIndex].pieces.map((piece, i) => (
                <Paper key={i} p="sm" radius="md" withBorder>
                  <Group gap={12}>
                    <ThemeIcon color={accentColors.primary.split(".")[0]} variant="light" size="md">
                      <IconHanger size={16} />
                    </ThemeIcon>
                    <Text fz="sm">{piece}</Text>
                  </Group>
                </Paper>
              ))}
            </Stack>

            {/* Outfit Navigation Dots */}
            <Group gap={8} mt="xl" justify="center">
              {typeData.outfitIdeas.map((_, i) => (
                <Box
                  key={i}
                  w={i === currentOutfitIndex ? 24 : 8}
                  h={8}
                  style={{
                    borderRadius: 4,
                    background: i === currentOutfitIndex ? accentColors.gradient : "var(--mantine-color-gray-3)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => setCurrentOutfitIndex(i)}
                />
              ))}
            </Group>
          </Card>
        </SimpleGrid>
      </Paper>

      {/* Do's and Don'ts */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" mb="xl">
        <Paper
          shadow="xs"
          p="xl"
          radius="md"
          withBorder
          style={{
            background: "linear-gradient(135deg, rgba(64, 192, 87, 0.08) 0%, rgba(64, 192, 87, 0.02) 100%)",
            border: "2px solid var(--mantine-color-green-3)",
          }}
        >
          <Group gap={12} mb={20}>
            <ThemeIcon color="green" size="xl" radius="xl" variant="filled">
              <IconCheck size={24} />
            </ThemeIcon>
            <Box>
              <Text fz="lg" fw={600}>{typeData.name} Style Tips</Text>
              <Text fz="xs" c="dimmed">Do this to nail the look</Text>
            </Box>
          </Group>
          <Stack gap={12}>
            {typeData.doStyles.map((tip, i) => (
              <Paper key={i} p="sm" radius="md" bg="white" withBorder style={{ borderColor: "var(--mantine-color-green-2)" }}>
                <Group gap={12} align="flex-start">
                  <IconCheck size={18} color="var(--mantine-color-green-6)" style={{ marginTop: 2, flexShrink: 0 }} />
                  <Text fz="sm">{tip}</Text>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Paper>

        <Paper
          shadow="xs"
          p="xl"
          radius="md"
          withBorder
          style={{
            background: "linear-gradient(135deg, rgba(250, 82, 82, 0.08) 0%, rgba(250, 82, 82, 0.02) 100%)",
            border: "2px solid var(--mantine-color-red-3)",
          }}
        >
          <Group gap={12} mb={20}>
            <ThemeIcon color="red" size="xl" radius="xl" variant="filled">
              <IconX size={24} />
            </ThemeIcon>
            <Box>
              <Text fz="lg" fw={600}>What to Avoid</Text>
              <Text fz="xs" c="dimmed">Skip these for {typeData.name}</Text>
            </Box>
          </Group>
          <Stack gap={12}>
            {typeData.dontStyles.map((tip, i) => (
              <Paper key={i} p="sm" radius="md" bg="white" withBorder style={{ borderColor: "var(--mantine-color-red-2)" }}>
                <Group gap={12} align="flex-start">
                  <IconX size={18} color="var(--mantine-color-red-6)" style={{ marginTop: 2, flexShrink: 0 }} />
                  <Text fz="sm">{tip}</Text>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* Celebrities */}
      <Paper shadow="xs" p="lg" radius="md" mb="xl" withBorder>
        <Group gap="xs" mb="md">
          <ThemeIcon size="md" variant="light" color={accentColors.primary.split(".")[0]}>
            <IconUsers size={16} />
          </ThemeIcon>
          <Title order={2} fz="lg">
            {typeData.name} Celebrity Inspiration
          </Title>
        </Group>
        <Text c="dimmed" mb="md">
          These celebrities embody the {typeData.name} aesthetic. Study their style for inspiration.
        </Text>
        <Group gap="sm" wrap="wrap">
          {typeData.celebrities.map((celeb, i) => (
            <Badge key={i} variant="light" color={accentColors.primary.split(".")[0]} size="xl" radius="md">
              {celeb}
            </Badge>
          ))}
        </Group>
      </Paper>

      {/* FAQ Section */}
      <Paper shadow="xs" p="lg" radius="md" mb="xl" withBorder>
        <Group gap="xs" mb="md">
          <ThemeIcon size="md" variant="light" color={accentColors.primary.split(".")[0]}>
            <IconQuestionMark size={16} />
          </ThemeIcon>
          <Title order={2} fz="lg">
            Frequently Asked Questions
          </Title>
        </Group>
        <Accordion variant="separated">
          {faqs.map((faq, i) => (
            <Accordion.Item key={i} value={`faq-${i}`}>
              <Accordion.Control>
                <Text fw={500}>{faq.question}</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                  {faq.answer}
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Paper>

      {/* Related Aesthetics */}
      <Paper shadow="xs" p="lg" radius="md" mb="xl" withBorder>
        <Title order={2} fz="lg" mb="md">
          Related Aesthetics
        </Title>
        <Text c="dimmed" mb="md">
          If you love {typeData.name}, you might also enjoy these related aesthetics:
        </Text>
        <Group gap="sm" wrap="wrap">
          {typeData.relatedAesthetics.map((related, i) => (
            <Button
              key={i}
              component={Link}
              href={`/aesthetic/${related}`}
              variant="light"
              color={accentColors.primary.split(".")[0]}
              size="md"
            >
              {related.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
            </Button>
          ))}
        </Group>
      </Paper>

      {/* CTA Section */}
      <Divider my="xl" />

      <Paper
        p={{ base: "xl", md: 48 }}
        radius="lg"
        ta="center"
        mb="xl"
        style={{
          background: accentColors.gradient,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          style={{
            position: "absolute",
            top: -60,
            left: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          style={{
            position: "absolute",
            bottom: -40,
            right: "10%",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Badge size="lg" variant="light" mb={16} style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
          Find Your Aesthetic
        </Badge>
        <Title order={2} fz={{ base: 24, md: 32 }} fw={400} mb={12} c="white">
          Not Sure If {typeData.name} Is Your Aesthetic?
        </Title>
        <Text fz="md" mb={32} maw={500} mx="auto" style={{ color: "rgba(255,255,255,0.9)" }}>
          Take our free aesthetic quiz to discover which style truly matches your personality, preferences, and lifestyle.
        </Text>
        <Button
          component={Link}
          href="/aesthetic-quiz"
          size="xl"
          color="white"
          rightSection={<IconArrowRight size={20} />}
          styles={{
            root: {
              color: `var(--mantine-color-${accentColors.primary})`,
              fontWeight: 600,
            },
          }}
        >
          Take the Free Aesthetic Quiz
        </Button>
      </Paper>

      <Stack gap="lg" align="center" ta="center">
        <Divider w="100%" label="or get a personalized analysis" labelPosition="center" />
        <QuizPhotoUpload quizName="Aesthetic Page" />
      </Stack>

      {/* Explore Other Aesthetics */}
      <Divider my="xl" />
      <Box>
        <Title order={2} fz={18} fw={500} mb={16}>
          Explore Other Aesthetics
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing={12}>
          {Object.entries(typeAccentColors)
            .filter(([id]) => id !== typeData.id)
            .map(([id]) => (
              <Button
                key={id}
                component={Link}
                href={`/aesthetic/${id}`}
                variant="default"
                size="sm"
              >
                {id.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
              </Button>
            ))}
        </SimpleGrid>
      </Box>
    </Container>
  );
}
